/**
 * The id a heading gets on the page.
 *
 * MarkdownRenderer writes it and the "On this page" list reads it back from
 * the rendered heading, so there is one algorithm and the list cannot link to
 * an id the page does not have.
 *
 * Make one slugger per document. It remembers the ids it has handed out, so:
 *
 * - a repeated heading ("Context" under two amendments) gets `context`,
 *   `context-1`, `context-2`: every heading can be linked to on its own;
 * - a heading whose slug is the name of a document or form property (`title`,
 *   `location`, `forms`, `links` …) gets `title-1`. The sanitiser removes an
 *   id like that (DOM clobbering), which left the heading with no id at all;
 * - a heading with no Latin letter or digit gets `section`, not an empty id.
 */
export function createHeadingSlugger(): (raw: string) => string {
  const used = new Set<string>();
  const form = typeof document === 'undefined' ? null : document.createElement('form');
  // The sanitiser's own test for an id it will remove.
  const clobbers = (id: string) => form !== null && (id in document || id in form);

  return (raw) => {
    const base = raw.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'section';
    let id = base;
    for (let n = 1; used.has(id) || clobbers(id); n += 1) id = `${base}-${n}`;
    used.add(id);
    return id;
  };
}
